import { useCallback, useMemo, useState } from "react";
import { AppId, FCX, getFormattedThousand, markMatch, ProductLogo, TextInput } from "@datassential/platform-ui-lib";
import { AiOutlineSearch } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { addFileToPackage } from "@actions/packages.actions";
import { setSharePopupState } from "@actions/documents.actions";
import { shareDataSelector } from "@selectors/documents.selectors";
import SharePopup from "@pages/_shared/SharePopup/SharePopup";
import { IMacListItem } from "@models/MacListItem";
import { useMacs } from "@apiHooks/useMacs";
import { useMacExport } from "@apiHooks/useMacExport";
import Table from "@components/new/Table/Table";
import { iTableConfigItem } from "@components/new/Table/Table.helpers";
import useTableSort from "@components/new/Table/useTableSort";
import Widget from "@components/Widget/Widget";
import MacsListActions from "../MacsListActions/MacsListActions";
import MacsListRowDescription from "../MacsListRowDescription/MacsListRowDescription";
import MacsListRowTooltips from "../MacsListRowTooltips/MacsListRowTooltips";
import MacsListRowLink from "../MacsListRowLink/MacsListRowLink";
import { MACS_DEFAULT_FIELDS_SORTING, MACS_SEARCH_PLACEHOLDER } from "./MacsList.constants";
import { filterAndRankMacs, getMacSharePayload } from "./MacsList.helpers";
import "./MacsList.scss";

const MacsList: FCX = () => {
    const dispatch = useDispatch();
    const isSharePopupOpen = useSelector(shareDataSelector);
    const [query, setQuery] = useState<string>('');
    const { data: { items: rawData }, isLoading } = useMacs();
    const { downloadingIds, exportMac } = useMacExport();
    const {
        sortedData,
        sortingDirection,
        sortingField,
        getColumnIconNoDefault,
        handleSort,
    } = useTableSort(rawData, 'dateSort', 'desc', MACS_DEFAULT_FIELDS_SORTING);

    const data = useMemo(() => filterAndRankMacs(sortedData, query), [sortedData, query]);

    const share = useCallback(
        (item: IMacListItem) => {
            dispatch(setSharePopupState(getMacSharePayload(item)));
        },
        [dispatch],
    );

    const addToPackage = useCallback(
        (id: number) => {
            toast.success('Item has been added to package');
            dispatch(addFileToPackage({ itemId: id, itemDocumentType: 'mac' }));
        },
        [dispatch],
    );

    const tableConfig = useMemo(
        (): iTableConfigItem[] => [
            {
                title: 'Country',
                icon: getColumnIconNoDefault('countryCodeStr', sortingField, sortingDirection),
                handleHeaderClick: handleSort('countryCodeStr'),
                styles: { cell: { flex: '0 0 80px', paddingLeft: 0 } },
                Value: ({ index }) => <>{data[index]?.countryCodeStr}</>,
            },
            {
                title: 'Name',
                icon: getColumnIconNoDefault('title', sortingField, sortingDirection),
                handleHeaderClick: handleSort('title'),
                styles: { cell: { flex: '2' } },
                getHtmlAttributes: (index, isHeader): Record<string, string> =>
                    isHeader ? {} : { 'data-tooltip-id': `macs-list-table-item-thumbnail-${data[index]?.id}` },
                Value: ({ index }) => (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: query.trim() === ''
                                ? data[index]?.title
                                : markMatch(data[index]?.title, query.trim(), true),
                        }}
                    />
                ),
            },
            {
                title: 'Description',
                icon: getColumnIconNoDefault('description', sortingField, sortingDirection),
                handleHeaderClick: handleSort('description'),
                styles: { cell: { flex: '2.5' } },
                Value: ({ index }) => <MacsListRowDescription query={query} row={data[index]} />,
            },
            {
                title: 'Items Count',
                icon: getColumnIconNoDefault('itemsCount', sortingField, sortingDirection),
                handleHeaderClick: handleSort('itemsCount'),
                styles: { cell: { flex: '0 0 120px', justifyContent: 'center', textAlign: 'center' } },
                Value: ({ index }) => <>{getFormattedThousand(data[index]?.itemsCount)}</>,
            },
            {
                title: 'Updated Date',
                icon: getColumnIconNoDefault('dateSort', sortingField, sortingDirection),
                handleHeaderClick: handleSort('dateSort'),
                styles: { cell: { flex: '0 0 150px', justifyContent: 'center', textAlign: 'center' } },
                Value: ({ index }) => <>{data[index]?.dateStr}</>,
            },
            {
                title: 'Actions',
                styles: { cell: { flex: '0 0 150px', paddingLeft: 0, justifyContent: 'center' } },
                Value: ({ index }) => (
                    <MacsListActions
                        id={data[index].id}
                        index={index}
                        isDownloading={downloadingIds.includes(data[index].id)}
                        onAddToPackage={() => addToPackage(data[index].id)}
                        onShare={() => share(data[index])}
                    />
                ),
            },
        ],
        [data, getColumnIconNoDefault, handleSort, sortingField, sortingDirection, query, downloadingIds, addToPackage, share],
    );

    return (
        <Widget className="MacsListTable" testId="blockMacsListTable">
            <div className="MacsListTable__wrapper">
                <div className="MacsListTable__table">
                    <div className="MacsListTable__table-search">
                        <TextInput
                            value={query}
                            setValue={setQuery}
                            placeholder={MACS_SEARCH_PLACEHOLDER}
                            className="MacsListTable__search"
                            testId="inputMacsSearch"
                        />
                        <AiOutlineSearch className="MacsListTable__search-icon"/>
                    </div>
                    {isLoading && (
                        <div className="MacsListTable__loading-overlay">
                            <div className="TrendingNow__spinner">
                                <ProductLogo appId={AppId.Snap} type="regular"/>
                            </div>
                        </div>
                    )}
                    <Table
                        isCopyButtonHidden
                        config={tableConfig}
                        rowsCount={data.length}
                        theme="light"
                        testId="tableMacs"
                        RowComponent={MacsListRowLink}
                        getRowProps={(index) => ({ macId: data[index]?.id })}
                    />
                    {data.map((item, index) => (
                        <MacsListRowTooltips
                            key={item.id}
                            item={item}
                            index={index}
                            query={query}
                            onExport={(format) => exportMac(item.id, format)}
                        />
                    ))}
                </div>
                {/* @ts-ignore -- SharePopup is a redux-connected JS component with no prop types (same as MacsListObsolete) */}
                {isSharePopupOpen && <SharePopup/>}
            </div>
        </Widget>
    );
};

export default MacsList;
